/**
 * Form field wrapper.
 *
 * Every input in the app is followed by its hint and its error in the same
 * order, so the wrapper owns that structure instead of each page repeating it.
 *
 * The hint and the error are also attached to the control they describe: a
 * screen reader has to hear why a field is invalid, not merely that it is, and
 * the ids therefore come from this wrapper rather than from each page. A caller
 * that already declared `aria-describedby` keeps its own ids, which are merged
 * with the ones added here.
 */

import { cloneElement, isValidElement, useId, type ComponentProps, type ReactNode } from 'react'

import { cn } from '../../lib/format'
import { Label } from './input'

export interface FieldProps {
  label: string
  htmlFor?: string
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
  children: ReactNode
}

/** ARIA attributes this wrapper adds to the rendered control. */
interface ControlAria {
  'aria-describedby'?: string
  'aria-errormessage'?: string
  'aria-invalid'?: boolean
}

/**
 * Returns the child control with the description attributes merged in.
 *
 * A non-element child (a fragment, a list, a composite that renders its own
 * input) is returned untouched: there is no single node the attributes could
 * describe, and inventing one would mislabel the control.
 */
function describeControl(node: ReactNode, aria: ControlAria): ReactNode {
  if (!isValidElement<ControlAria>(node)) {
    return node
  }
  const declared = node.props['aria-describedby']
  const describedBy = [declared, aria['aria-describedby']]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ')
  return cloneElement(node, {
    ...(describedBy.length > 0 ? { 'aria-describedby': describedBy } : {}),
    ...(aria['aria-errormessage'] !== undefined
      ? { 'aria-errormessage': aria['aria-errormessage'] }
      : {}),
    ...(aria['aria-invalid'] !== undefined ? { 'aria-invalid': aria['aria-invalid'] } : {}),
  })
}

export function Field({ label, htmlFor, hint, error, required, className, children }: FieldProps) {
  // One prefix per field instance, so hint and error ids are stable across
  // renders and unique even when a page gave the input no id of its own.
  const fieldId = useId()
  const hintId = `${fieldId}-hint`
  const errorId = `${fieldId}-error`

  // The hint is hidden while an error is shown, exactly as it is rendered below,
  // so the reader is never given two half sentences.
  const describedBy = [hint && !error ? hintId : null, error ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ')

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required ? <span className="text-destructive ml-1">*</span> : null}
      </Label>
      {describeControl(children, {
        ...(describedBy.length > 0 ? { 'aria-describedby': describedBy } : {}),
        // `aria-errormessage` is only honoured together with `aria-invalid`, and
        // an `error` being rendered is what makes this control invalid.
        ...(error ? { 'aria-errormessage': errorId, 'aria-invalid': true } : {}),
      })}
      {hint && !error ? (
        <p id={hintId} className="text-muted-foreground text-xs">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-destructive text-xs">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Vertical stack used inside forms and wizard steps. */
export function FieldGroup({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-4', className)} {...props} />
}
